import { useMemo } from 'react'
import { useStagedActionsQuery } from '../../../hooks/staged-actions/query'
import type { ActionReference, StagedAction } from '../../../hooks/staged-actions/types'

/**
 * The actions staged for this meeting, oldest first. They arrive through the
 * staged-actions list the gateway pushes updates for, so nothing here polls
 * or detects.
 *
 * Every planner, not just v2: Recap's For You lists what a meeting produced
 * without asking which planner produced it, and a list beside it that quietly
 * dropped everything else read as broken rather than scoped.
 */
export function useMeetingActions(
  audioSessionId: string | undefined,
  /** The saved note, which the meeting's final planner run often files its actions under instead. */
  audioNoteId?: string,
): { actions: Array<StagedAction> } {
  const { allActions } = useStagedActionsQuery()

  return useMemo(() => {
    if (!audioSessionId && !audioNoteId) return { actions: [] }
    const actions = allActions
      .filter(
        (action) =>
          !action.archivedAt &&
          action.references?.some((reference) => isMeetingReference(reference, audioSessionId, audioNoteId)),
      )
      .sort((a, b) => a.createdAt.localeCompare(b.createdAt))
    return { actions }
  }, [allActions, audioSessionId, audioNoteId])
}

export function isMeetingReference(
  reference: ActionReference,
  audioSessionId: string | null | undefined,
  audioNoteId: string | null | undefined,
): boolean {
  return (
    (!!audioSessionId && reference.audioSessionId === audioSessionId) ||
    (!!audioNoteId && reference.audioNoteId === audioNoteId)
  )
}
